import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calculator, CalendarDays, ListChecks, MapPin, TrendingUp, ArrowRight, Plus, DollarSign,
  Sparkles, BarChart3, Crown, Award, AlertTriangle, Package,
} from "lucide-react";
import {
  getEvents, getChecklistProgress, getEventSummary, formatCurrency,
} from "../lib/storage";
import { getProducts, getEventProductSummary, getProductsLibrarySummary } from "../lib/products";
import { useProState } from "../lib/pro";
import ProBadge from "../components/ProBadge";
import UpgradeButton from "../components/UpgradeButton";

const CHECKLIST_TOTAL = 21;

const verdictTone = {
  good: "bg-brand-success/10 text-brand-success border-brand-success/30",
  risky: "bg-brand-warning/15 text-brand-warning border-brand-warning/30",
  bad: "bg-brand-error/10 text-brand-error border-brand-error/30",
  neutral: "bg-brand-cream-soft text-brand-mute border-brand-line",
};

export default function Dashboard() {
  const { pro } = useProState();
  const [events, setEvents] = useState([]);
  const [checklistPct, setChecklistPct] = useState(0);
  const [productCount, setProductCount] = useState(0);
  const [librarySummary, setLibrarySummary] = useState({ count: 0, catalogValue: 0, bestMarginProduct: null });

  useEffect(() => {
    setEvents(getEvents().sort((a, b) => (a.date || "").localeCompare(b.date || "")));
    setChecklistPct(getChecklistProgress("global", CHECKLIST_TOTAL).pct);
    const lib = getProductsLibrarySummary();
    setLibrarySummary(lib);
    setProductCount(lib.count);
  }, []);

  const enriched = useMemo(() => events.map((e) => ({ event: e, summary: getEventSummary(e), productSummary: getEventProductSummary(e) })), [events]);

  const productInsights = useMemo(() => {
    let totalRev = 0;
    let totalProfit = 0;
    let totalUnsold = 0;
    const sellerTotals = {};
    enriched.forEach(({ productSummary: ps }) => {
      totalRev += ps.totalRevenue;
      totalProfit += ps.totalProfit;
      totalUnsold += ps.totalUnsold;
      ps.lines.forEach(({ product, line }) => {
        if (product && line.qtySold > 0) {
          const key = product.id;
          if (!sellerTotals[key]) sellerTotals[key] = { product, qty: 0, revenue: 0 };
          sellerTotals[key].qty += line.qtySold;
          sellerTotals[key].revenue += line.revenue;
        }
      });
    });
    let topSeller = null;
    Object.values(sellerTotals).forEach((s) => {
      if (!topSeller || s.qty > topSeller.qty) topSeller = s;
    });
    return { totalRev, totalProfit, totalUnsold, topSeller };
  }, [enriched]);

  const insights = useMemo(() => {
    let totalEst = 0;
    let totalActual = 0;
    let bestEst = null;
    let bestActual = null;
    let worthRepeating = 0;
    let needsReview = 0;
    enriched.forEach(({ event, summary }) => {
      totalEst += summary.estimate.profit;
      if (summary.actual) {
        totalActual += summary.actual.profit;
        if (!bestActual || summary.actual.profit > bestActual.profit) {
          bestActual = { name: event.name, id: event.id, profit: summary.actual.profit };
        }
      }
      if (!bestEst || summary.estimate.profit > bestEst.profit) {
        bestEst = { name: event.name, id: event.id, profit: summary.estimate.profit };
      }
      if (summary.repeat.key === "worth") worthRepeating += 1;
      if (summary.repeat.key === "needs-review") needsReview += 1;
    });
    return { totalEst, totalActual, bestEst, bestActual, worthRepeating, needsReview };
  }, [enriched]);

  const today = new Date(new Date().toDateString());
  const upcoming = events.filter((e) => e.date && new Date(e.date + "T00:00:00") >= today);
  const nextEvent = upcoming[0] || events[0];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 pb-24 md:pb-16" data-testid="dashboard-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-8">
        <div>
          <p className="label-eyebrow">Dashboard</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-brand-ink mt-2">
            Your vendor overview
          </h1>
          <p className="text-brand-mute text-sm mt-1.5">Track what worked, what didn't, and what's next.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!pro && <UpgradeButton variant="primary" label="Upgrade — $9/month" testid="dash-upgrade-btn" />}
          <Link to="/planner" className="pop-btn-secondary" data-testid="dash-add-event">
            <Plus className="w-4 h-4" /> Add event
          </Link>
        </div>
      </div>

      {/* Primary stats */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={CalendarDays} label="Total events" value={events.length} testid="stat-total-events" />
        <StatCard icon={TrendingUp} label="Upcoming" value={upcoming.length} testid="stat-upcoming" />
        <StatCard icon={DollarSign} label="Total est. profit" value={formatCurrency(insights.totalEst)} testid="stat-total-est" />
        <StatCard icon={DollarSign} label="Total actual profit" value={formatCurrency(insights.totalActual)} testid="stat-total-actual" pro highlight />
      </section>

      {/* Secondary insights */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <InsightCard
          icon={Award}
          label="Best estimated event"
          value={insights.bestEst?.name || "—"}
          sub={insights.bestEst ? formatCurrency(insights.bestEst.profit) : "Add events to see this"}
          to={insights.bestEst ? `/event/${insights.bestEst.id}` : "/planner"}
          testid="insight-best-est"
        />
        <InsightCard
          icon={Crown}
          label="Best actual event"
          value={insights.bestActual?.name || "—"}
          sub={insights.bestActual ? formatCurrency(insights.bestActual.profit) : "Complete a report"}
          to={insights.bestActual ? `/event/${insights.bestActual.id}` : "/planner"}
          testid="insight-best-actual"
          pro
        />
        <InsightCard
          icon={Sparkles}
          label="Worth repeating"
          value={`${insights.worthRepeating}`}
          sub="events recommended"
          to="/compare"
          testid="insight-worth"
        />
        <InsightCard
          icon={AlertTriangle}
          label="Needs review"
          value={`${insights.needsReview}`}
          sub="check costs or sales"
          to="/compare"
          testid="insight-review"
          warn
        />
      </section>

      {/* Quick links */}
      <section className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        <QuickLink to="/calculator" icon={Calculator} title="Calculate Profit" subtitle="Run event numbers" testid="quick-calculator" />
        <QuickLink to="/planner" icon={CalendarDays} title="Plan Event" subtitle="Add or edit events" testid="quick-planner" />
        <QuickLink to="/products" icon={Package} title="Products" subtitle={productCount > 0 ? `${productCount} saved` : "Build your library"} testid="quick-products" pro />
        <QuickLink to="/checklist" icon={ListChecks} title="Checklist" subtitle={`${checklistPct}% ready`} testid="quick-checklist" />
        <QuickLink to="/compare" icon={BarChart3} title="Compare" subtitle="Side-by-side outcomes" testid="quick-compare" pro />
      </section>

      {/* Products summary card */}
      <section className="mb-10" data-testid="dash-products-summary">
        <p className="label-eyebrow mb-3">Products</p>
        <div className="pop-card p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="w-11 h-11 rounded-2xl bg-brand-green/10 text-brand-green grid place-items-center">
                <Package className="w-6 h-6" strokeWidth={2} />
              </span>
              <div>
                <p className="font-heading text-lg font-semibold text-brand-ink flex items-center gap-2">
                  Product Library <ProBadge />
                </p>
                <p className="text-xs text-brand-mute mt-0.5">
                  Used across {enriched.filter((e) => (e.event.eventProducts || []).length > 0).length} event{enriched.filter((e) => (e.event.eventProducts || []).length > 0).length === 1 ? "" : "s"}
                </p>
              </div>
            </div>
            <Link to="/products" className="pop-btn-secondary" data-testid="dash-products-link">
              <Plus className="w-4 h-4" /> {productCount > 0 ? "Manage" : "Add product"}
            </Link>
          </div>

          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <MiniStatCard
              label="Total saved products"
              value={`${librarySummary.count}`}
              sub={librarySummary.count === 0 ? "Add your first product" : `${librarySummary.count === 1 ? "product" : "products"} in library`}
              testid="dash-prod-count"
            />
            <MiniStatCard
              label="Catalog value"
              value={formatCurrency(librarySummary.catalogValue)}
              sub="1 of each at sale price"
              testid="dash-prod-catalog-value"
              highlight={librarySummary.catalogValue > 0}
            />
            <MiniStatCard
              label="Best margin product"
              value={librarySummary.bestMarginProduct ? librarySummary.bestMarginProduct.product.name : "—"}
              sub={librarySummary.bestMarginProduct ? `${librarySummary.bestMarginProduct.margin.toFixed(1)}% margin` : "Add cost & sale price"}
              testid="dash-prod-best-margin"
            />
          </div>
        </div>
      </section>

      {/* Next event highlight */}
      {nextEvent && (
        <section className="mb-10">
          <p className="label-eyebrow mb-3">Next up</p>
          <div className="relative overflow-hidden rounded-3xl bg-brand-green text-white p-6 sm:p-8" data-testid="dash-next-event">
            <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-brand-gold/20 blur-3xl" />
            <div className="relative grid sm:grid-cols-3 gap-6 items-center">
              <div className="sm:col-span-2">
                <Sparkles className="w-6 h-6 text-brand-gold" strokeWidth={2} />
                <h3 className="font-heading text-2xl sm:text-3xl font-semibold mt-3">{nextEvent.name}</h3>
                <div className="mt-3 flex flex-wrap gap-4 text-sm text-white/85">
                  {nextEvent.date && (
                    <span className="flex items-center gap-1.5">
                      <CalendarDays className="w-4 h-4" strokeWidth={2} />
                      {new Date(nextEvent.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}
                    </span>
                  )}
                  {nextEvent.location && (
                    <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" strokeWidth={2} /> {nextEvent.location}</span>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <Link to={`/event/${nextEvent.id}`} className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-ink hover:brightness-95 rounded-full px-5 py-2.5 font-medium transition-all" data-testid="dash-next-open">
                  Open event →
                </Link>
                <Link to="/checklist" className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white border border-white/20 rounded-full px-5 py-2.5 font-medium transition-all" data-testid="dash-next-checklist">
                  <ListChecks className="w-4 h-4" /> Open checklist
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Events list */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <p className="label-eyebrow">Saved events</p>
          <Link to="/planner" className="text-sm font-medium text-brand-green hover:underline">Manage →</Link>
        </div>

        {events.length === 0 ? (
          <div className="pop-card p-10 text-center" data-testid="dash-empty">
            <span className="inline-grid place-items-center w-14 h-14 rounded-2xl bg-brand-green/10 text-brand-green">
              <CalendarDays className="w-7 h-7" strokeWidth={2} />
            </span>
            <h3 className="font-heading text-xl font-semibold text-brand-ink mt-4">No events yet</h3>
            <p className="text-brand-mute text-sm mt-1.5">Add your first event to see it here.</p>
            <Link to="/planner" className="pop-btn-primary mt-5 inline-flex" data-testid="dash-empty-add">
              <Plus className="w-4 h-4" /> Add event
            </Link>
          </div>
        ) : (
          <ul className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {enriched.map(({ event, summary }) => {
              const dateLabel = event.date
                ? new Date(event.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                : "Date TBD";
              return (
                <li key={event.id}>
                  <Link
                    to={`/event/${event.id}`}
                    className="pop-card pop-card-hover p-5 block h-full"
                    data-testid={`dash-event-${event.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h4 className="font-heading text-base font-semibold text-brand-ink truncate">{event.name}</h4>
                        <p className="text-xs text-brand-mute mt-1 flex items-center gap-1.5">
                          <CalendarDays className="w-3.5 h-3.5" strokeWidth={2} /> {dateLabel}
                        </p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-brand-mute shrink-0 mt-1" />
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <MiniStat label="Est." value={formatCurrency(summary.estimate.profit)} />
                      <MiniStat label="Actual" value={summary.actual ? formatCurrency(summary.actual.profit) : "—"} good={!!summary.actual} muted={!summary.actual} />
                    </div>
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      <span className={`pop-pill border text-[10px] ${verdictTone[summary.repeat.tone]}`}>
                        {summary.repeat.label}
                      </span>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid, pro = false, highlight = false }) {
  return (
    <div className="pop-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-brand-mute">
          <Icon className="w-4 h-4" strokeWidth={2} />
          <p className="text-[11px] uppercase tracking-wider font-semibold">{label}</p>
        </div>
        {pro && <ProBadge />}
      </div>
      <p className={`font-heading text-2xl sm:text-3xl font-semibold mt-2 tracking-tight ${highlight ? "text-brand-green" : "text-brand-ink"}`}>{value}</p>
    </div>
  );
}

function InsightCard({ icon: Icon, label, value, sub, to, testid, pro = false, warn = false }) {
  return (
    <Link to={to} className="pop-card pop-card-hover p-5 block h-full" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <span className={`w-9 h-9 rounded-xl grid place-items-center ${warn ? "bg-brand-warning/15 text-brand-warning" : "bg-brand-green/10 text-brand-green"}`}>
          <Icon className="w-5 h-5" strokeWidth={2} />
        </span>
        {pro && <ProBadge />}
      </div>
      <p className="text-[10px] uppercase tracking-wider font-semibold text-brand-mute mt-3">{label}</p>
      <p className="font-heading text-base font-semibold text-brand-ink mt-1 truncate">{value}</p>
      <p className="text-xs text-brand-mute mt-0.5 truncate">{sub}</p>
    </Link>
  );
}

function QuickLink({ to, icon: Icon, title, subtitle, testid, pro = false }) {
  return (
    <Link to={to} className="pop-card pop-card-hover p-5 flex items-center gap-4 relative" data-testid={testid}>
      <span className="w-12 h-12 grid place-items-center rounded-2xl bg-brand-green/10 text-brand-green shrink-0">
        <Icon className="w-6 h-6" strokeWidth={2} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="font-heading text-base font-semibold text-brand-ink">{title}</p>
          {pro && <ProBadge />}
        </div>
        <p className="text-xs text-brand-mute mt-0.5 truncate">{subtitle}</p>
      </div>
      <ArrowRight className="w-4 h-4 text-brand-mute shrink-0" />
    </Link>
  );
}

function MiniStat({ label, value, good = false, muted = false }) {
  return (
    <div className="rounded-lg bg-brand-cream-soft p-2.5">
      <p className="text-[9px] uppercase tracking-wider font-semibold text-brand-mute">{label}</p>
      <p className={`font-heading text-sm font-semibold mt-0.5 tabular-nums ${muted ? "text-brand-mute/70" : good ? "text-brand-green" : "text-brand-ink"}`}>{value}</p>
    </div>
  );
}

function MiniStatCard({ label, value, sub, testid, highlight = false, negative = false }) {
  return (
    <div className="rounded-xl bg-brand-cream-soft p-3" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-wider font-semibold text-brand-mute">{label}</p>
      <p className={`font-heading text-base sm:text-lg font-semibold mt-1 tabular-nums truncate ${negative ? "text-brand-error" : highlight ? "text-brand-green" : "text-brand-ink"}`}>{value}</p>
      {sub && <p className="text-[10px] text-brand-mute mt-0.5 truncate">{sub}</p>}
    </div>
  );
}
